import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';

export function Dashboard() {
  const { user } = useAuthStore();
  const [health, setHealth] = useState<any>(null);
  const [prompts, setPrompts] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.health(),
      api.getPrompts(),
    ])
      .then(([healthData, promptsData]) => {
        setHealth(healthData);
        setPrompts(promptsData);
      })
      .catch((error) => {
        console.error('Failed to load dashboard data:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-linepe-orange mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome, {user?.name}! 👋
        </h1>
        <p className="text-gray-600 mt-2">Here's an overview of your Content API system</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Total Prompts</p>
              <p className="text-3xl font-bold text-gray-900">
                {prompts?.prompts?.length || 0}
              </p>
            </div>
            <div className="text-4xl">📝</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Provider Status</p>
              <p className="text-lg font-semibold text-green-600 flex items-center gap-2">
                {health?.ok ? '✅ Healthy' : '⚠️ Issues'}
              </p>
            </div>
            <div className="text-4xl">🤖</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">Current Provider</p>
              <p className="text-lg font-semibold text-gray-900">
                {health?.env?.mode || 'N/A'}
              </p>
            </div>
            <div className="text-4xl">⚡</div>
          </div>
        </div>
      </div>

      {/* Provider Info */}
      {health?.env?.providers && (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Active Providers</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <p className="font-semibold">Text Generation</p>
                <p className="text-sm text-gray-600">
                  {health.env.providers.text?.current} ({health.env.providers.text?.model})
                </p>
              </div>
              <span className="text-green-600">✓</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <p className="font-semibold">Image Generation</p>
                <p className="text-sm text-gray-600">
                  {health.env.providers.image?.current} ({health.env.providers.image?.model})
                </p>
              </div>
              <span className="text-green-600">✓</span>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 border-2 border-gray-200 hover:border-linepe-orange rounded-lg transition-colors text-center">
            <div className="text-3xl mb-2">➕</div>
            <div className="text-sm font-semibold">New Prompt</div>
          </button>
          <button className="p-4 border-2 border-gray-200 hover:border-linepe-orange rounded-lg transition-colors text-center">
            <div className="text-3xl mb-2">🎨</div>
            <div className="text-sm font-semibold">Generate Card</div>
          </button>
          <button className="p-4 border-2 border-gray-200 hover:border-linepe-orange rounded-lg transition-colors text-center">
            <div className="text-3xl mb-2">🔑</div>
            <div className="text-sm font-semibold">API Keys</div>
          </button>
          <button className="p-4 border-2 border-gray-200 hover:border-linepe-orange rounded-lg transition-colors text-center">
            <div className="text-3xl mb-2">📊</div>
            <div className="text-sm font-semibold">Analytics</div>
          </button>
        </div>
      </div>
    </div>
  );
}

